import { z } from 'zod';
import { amountSchema } from '../common/amount.js';
import { isoDateTimeSchema, localeSchema } from '../common/primitives.js';
import { bilingualTextSchema, currencyRefSchema } from '../common/refs.js';
import { customerMatchSchema, intendedRecipientViewSchema, partyIdentityViewSchema, } from '../deposits/parties.js';
/**
 * Tablets (cash-deposits.md section 6). The customer-facing tablet is the
 * shared tablet service (tablet.md): it is paired there and serves every
 * platform. Cash deposits sends its forms and scans to it as the connected
 * system `DEPOSITS`, follows each task by polling, and keeps its own copy of
 * each submission, signature and document photo.
 */
/** GET /tablets/options — active tablets of the session branch, from the tablet service. */
export const tabletOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Holds its live connection to the tablet service right now. */
    online: z.boolean(),
    /** Shows an open task of any platform right now. */
    busy: z.boolean(),
    lastSeenAt: isoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------
/**
 * - DEPOSITOR_FORM: depositor data, intended recipient, photo, declaration, signature (4.1).
 * - RECIPIENT_FORM: actual recipient data, photo, acknowledgement, signature (5.2).
 * - RETURN_FORM: the depositor identifies and signs to take the cash back (5.4).
 * - DEPOSITOR_RESIGN: the depositor signs again after a substantive edit (8.3).
 * - SCAN: the tablet camera reads the QR and returns the reference (5.1).
 */
export const TABLET_TASK_KINDS = [
    'DEPOSITOR_FORM',
    'RECIPIENT_FORM',
    'RETURN_FORM',
    'DEPOSITOR_RESIGN',
    'SCAN',
];
export const tabletTaskKindSchema = z.enum(TABLET_TASK_KINDS);
/**
 * SENT: shown (or waiting to be shown) on the tablet. SUBMITTED: the customer
 * pressed تم and Cash deposits holds its own copy of the submission.
 * COMPLETED: the operation was confirmed. CANCELLED: the employee went back,
 * changed signed data (the signature is void) or sent a newer task, or the
 * tablet service closed it (cancelled there, expired, or the tablet was
 * disabled).
 */
export const TABLET_TASK_STATUSES = ['SENT', 'SUBMITTED', 'COMPLETED', 'CANCELLED'];
export const tabletTaskStatusSchema = z.enum(TABLET_TASK_STATUSES);
/** Signatures are PNG; document photos JPEG of at most 5 MB (section 12). */
export const DOCUMENT_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const SIGNATURE_MAX_BYTES = 512 * 1024;
/**
 * What the tablet shows for its current task. It never carries stored
 * customer data from Customers; `prefill` only returns what the customer typed
 * earlier in the same operation (a form sent back after its signature was
 * voided) or, for DEPOSITOR_RESIGN, the edited deposit being signed.
 */
export const tabletTaskPayloadSchema = z.object({
    id: z.string(),
    kind: tabletTaskKindSchema,
    /** Language the customer last chose on this tablet, or the deposit's. */
    language: localeSchema,
    amount: amountSchema.nullable(),
    currency: currencyRefSchema.nullable(),
    /** Declaration (deposit) or acknowledgement (delivery, return) from settings. */
    text: bilingualTextSchema.nullable(),
    prefill: z
        .object({
        person: partyIdentityViewSchema.nullable(),
        intendedRecipient: intendedRecipientViewSchema.nullable(),
    })
        .nullable(),
});
/** Employee view of a task: what the customer entered, beside the Customers match. */
export const tabletTaskViewSchema = z.object({
    id: z.string(),
    kind: tabletTaskKindSchema,
    status: tabletTaskStatusSchema,
    tablet: z.object({
        id: z.string(),
        name: z.string(),
        online: z.boolean(),
        lastSeenAt: isoDateTimeSchema.nullable(),
    }),
    depositId: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    submittedAt: isoDateTimeSchema.nullable(),
    submission: z
        .object({
        language: localeSchema,
        /** Depositor (DEPOSITOR_FORM) or the person collecting (RECIPIENT_FORM, RETURN_FORM). */
        person: partyIdentityViewSchema.nullable(),
        intendedRecipient: intendedRecipientViewSchema.nullable(),
        signature: z.object({ id: z.string(), signedAt: isoDateTimeSchema }).nullable(),
        documentPhoto: z.object({ id: z.string() }).nullable(),
        customerMatch: customerMatchSchema.nullable(),
    })
        .nullable(),
    /** SCAN only: the reference read by the camera. */
    scannedReference: z.string().nullable(),
});
/** POST /tablet-tasks/scan — ask the tablet to open its camera. */
export const startScanSchema = z.object({ tabletId: z.string().min(1) });
/** POST /tablet-tasks/:id/resend — إعادة الإرسال إلى التابلت, optionally to another tablet. */
export const resendTabletTaskSchema = z.object({ tabletId: z.string().min(1).optional() });
//# sourceMappingURL=contract.js.map